import { SPECIES_PREVIEW_CACHE_SESSION_KEY } from "@/constants/browserStorageKeys";
import { Species } from "@/data/species/types/Species";

type PreviewCacheEntry = {
  byTaxonId: Record<string, Species>;
  bySlug: Record<string, Species>;
};

const memoryCache: PreviewCacheEntry = { byTaxonId: {}, bySlug: {} };

function readSessionCache(): PreviewCacheEntry {
  if (typeof window === "undefined") return memoryCache;
  try {
    const raw = window.sessionStorage.getItem(SPECIES_PREVIEW_CACHE_SESSION_KEY);
    if (!raw) return memoryCache;
    const parsed = JSON.parse(raw) as PreviewCacheEntry;
    if (!parsed?.byTaxonId || !parsed?.bySlug) return memoryCache;
    Object.assign(memoryCache.byTaxonId, parsed.byTaxonId);
    Object.assign(memoryCache.bySlug, parsed.bySlug);
    return memoryCache;
  } catch {
    return memoryCache;
  }
}

function writeSessionCache(cache: PreviewCacheEntry) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      SPECIES_PREVIEW_CACHE_SESSION_KEY,
      JSON.stringify(cache),
    );
  } catch {
    // ignore
  }
}

export function getCachedPreviewSpecies(opts: {
  taxonId?: number | null;
  slug?: string | null;
}): Species | null {
  const cache = readSessionCache();
  if (opts.taxonId != null) {
    const hit = cache.byTaxonId[String(opts.taxonId)];
    if (hit) return hit;
  }
  if (opts.slug?.trim()) {
    const hit = cache.bySlug[opts.slug.trim().toLowerCase()];
    if (hit) return hit;
  }
  return null;
}

export function registerPreviewSpecies(species: Species): void {
  if (!species?.taxonId || !species.slug) return;
  const cache = readSessionCache();
  cache.byTaxonId[String(species.taxonId)] = species;
  cache.bySlug[species.slug.trim().toLowerCase()] = species;
  writeSessionCache(cache);
}

export function getAllCachedPreviews(): Species[] {
  const cache = readSessionCache();
  const seen = new Set<number>();
  const out: Species[] = [];
  for (const s of Object.values(cache.byTaxonId)) {
    if (!s?.taxonId || seen.has(s.taxonId)) continue;
    seen.add(s.taxonId);
    out.push(s);
  }
  return out;
}
